import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import clsx from 'clsx'
import {
  updatePagination,
} from '@/redux/ducks/lists'
import { SelectInput } from '@/modules/base/components/inputs'

const { pagination: { label } } = lang
const options = [{ label: '20', value: 20 }, { label: '50', value: 50 }, { label: '100', value: 100 }, { label: '250', value: 250 }, { label: '1000', value: 1000 }, { label: '10000', value: 10000 }, { label: lang.generics.all, value: 999999 }]

const useStyles = makeStyles((theme) => ({
  container: {
    background: theme.white,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 10,
    borderTop: `1px solid ${theme.borderColor}`,
    height: 63,
  },
  select: {
    width: 80,
  },
  label: {
    paddingRight: 8,
  },
  buttons: {
    verticalAlign: 'middle',
    display: 'flex',
    alignItems: 'center',
  },
  dots: {
    paddingBottom: '0.5em',
  },
  icons: {
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: 10,
    cursor: 'pointer',
  },
  deactivate: {
    color: theme.textLight,
  },
  jumpButton: {
    paddingLeft: 5,
    paddingRight: 5,
    cursor: 'pointer',
    width: 18,
    height: 18,
    textAlign: 'left',
    fontSize: 12,
    border: '1px solid',
    borderRadius: 4,
    borderColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  active: {
    background: theme.primary,
    color: theme.white,
  },
}))

const PaginationV2 = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const listState = useSelector(state => state.lists)
  const { listSettingName } = props
  const pagination = listState[listSettingName].pagination

  const { limit, count: totalCount, offset } = pagination

  const pageCount = totalCount ? Math.trunc(((totalCount - 1) / limit)) : 0
  const isFirst = offset === 0
  const isLast = offset === pageCount
  if (offset > pageCount) {
    dispatch(updatePagination(listSettingName, { offset: pageCount }))
  }

  const onLimitChange = (option) => {
    dispatch(updatePagination(listSettingName, { limit: parseInt(option.value) }))
  }

  const moveTo = (index) => {
    if (index !== offset) {
      dispatch(updatePagination(listSettingName, { offset: index }))
    }
  }

  const moveFirst = () => {
    if (!isFirst) {
      dispatch(updatePagination(listSettingName, { offset: 0 }))
    }
  }

  const moveLast = () => {
    if (!isLast) {
      dispatch(updatePagination(listSettingName, { offset: pageCount }))
    }
  }

  const generateButton = (index, pageCount) => {
    const before = index - 1
    const after = index + 1

    const result = []
    if (before === 1) {
      result.push((<div key='jbs' className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-1`} onClick={() => moveTo(0)}>1</div>))
    }
    if (before > 1) {
      result.push((<div key='jbs' className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-1`} onClick={() => moveTo(0)}>1</div>))
      result.push((<div key={`jbld${index}`} className={classes.dots}>&hellip;</div>))
    }
    if (before >= 0) {
      result.push(<div key={`jb${before}`} className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-${before + 1}`} onClick={() => moveTo(before)}>{before + 1}</div>)
    }
    // current page
    result.push(<div key={`jb${index}`} className={clsx(classes.jumpButton, index === offset ? classes.active : null)} data-qs-id={`${listSettingName}-pagination-v2-page-${index + 1}`} aria-current='page'>{index + 1}</div>)
    if (after <= pageCount) {
      result.push(<div key={`jbr${pageCount}`} className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-${after + 1}`} onClick={() => moveTo(after)}>{after + 1}</div>)
    }
    if (after < pageCount - 1) {
      result.push(<div key={`jbrd${index}`} className={classes.dots}>&hellip;</div>)
      result.push((<div key={`jbe${pageCount}`} className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-${pageCount + 1}`} onClick={() => moveTo(pageCount)}>{pageCount + 1}</div>))
    }
    if (after === pageCount - 1) {
      result.push((<div key={`jbe${pageCount}`} className={classes.jumpButton} data-qs-id={`${listSettingName}-pagination-v2-page-${pageCount + 1}`} onClick={() => moveTo(pageCount)}>{pageCount + 1}</div>))
    }
    return result
  }

  const customSelectStyle = useMemo(() => {
    return {
      control: (base) => {
        return {
          ...base,
          fontSize: 13,
          height: '28px',
          minHeight: '28px',
        }
      },
      menu: (base) => {
        return {
          ...base,
          overflow: 'hidden',
        }
      },
      option: (base) => {
        return {
          ...base,
          fontSize: 13,
        }
      },
      indicatorsContainer: (base, state) => {
        return {
          ...base,
          '& > div': {
            padding: '2px 6px',
          },
        }
      },
    }
  })

  return (
    <div className={classes.container} data-qs-id={`${listSettingName}-pagination-v2`}>
      <div className={classes.label}>{label}</div>
      <SelectInput
        allowCreate={false}
        notAsync
        dataQsId={`${listSettingName}-pagination`}
        options={options.map(option => ({ label: option.label, value: option.value }))}
        value={limit}
        handleChange={onLimitChange}
        containerClass={classes.select}
        styles={customSelectStyle}
        customSelectOptions={{
          menuPlacement: 'top',
        }}
      />
      <div className={classes.buttons}>
        <div className={clsx('icon icon-arrow_left', classes.icons, isFirst ? classes.deactivate : null)} data-qs-id={`${listSettingName}-pagination-v2-first`} onClick={moveFirst} />
        {generateButton(offset, pageCount)}
        <div className={clsx('icon icon-arrow_right', classes.icons, isLast ? classes.deactivate : null)} data-qs-id={`${listSettingName}-pagination-v2-last`} onClick={moveLast} />
      </div>
    </div>
  )
}

PaginationV2.propTypes = {
  gridApi: PropTypes.object,
  listSettingName: PropTypes.string.isRequired,
}

export default PaginationV2
